import React from 'react';
import { LanguageProvider, useTranslation, LanguageSelector } from './i18n.js';
import { ThemeToggle } from './theme.js';
import { Footer } from './footer.js';
import { ScrollToTop } from './scrollToTop.js';
import { CookieConsentManager } from './cookieConsentUI.js';
import { PasswordGate, isSiteUnlocked } from './passwordGate.js';
import { decodeHtmlEntities } from './htmlEntities.js';
import { ALL_COLLECTIONS, collectionPath, productPath } from './collectionContext.js';

// Repli quand `book.direction` (renvoyé par l'API book) est absent/invalide — cas résiduel (ancien
// contenu en cache) : détection par script Unicode dans le titre.
const RTL_UNICODE_RANGE = /[֑-߿יִ-﷽ﹰ-ﻼ]/;

function detectDirection(text) {
  return RTL_UNICODE_RANGE.test(String(text || '')) ? 'rtl' : 'ltr';
}

function BookDistributor({ distributor, t }) {
  const link = distributor.url || distributor.site;

  return (
    <div className="book-distributor">
      {distributor.flag && (
        <img className="book-distributor__flag" src={distributor.flag} alt={distributor.country || ''} loading="lazy" />
      )}
      <div className="book-distributor__body">
        {distributor.country && (
          <p className="book-distributor__country">{decodeHtmlEntities(distributor.country)}</p>
        )}
        {link && (
          <a className="book-distributor__cta" href={link} target="_blank" rel="noopener noreferrer">
            {distributor.url ? t('bookViewBuyLabel') : t('bookVisitSiteLabel')}
          </a>
        )}
        {distributor.email && (
          <a className="book-distributor__email" href={`mailto:${distributor.email}`}>
            {distributor.email}
          </a>
        )}
      </div>
    </div>
  );
}

// Produit lié (`altreferences` de l'API book : autre format/support du même titre, ex. version CD
// d'un livre) : référence, titre, couverture, collection et slug de sa propre page /book/:slug.
// Deux liens dans la carte (produit, collection) : un <a> ne pouvant pas en contenir un autre, la
// carte est un bloc dont le lien du titre est étendu à toute sa surface (::after, voir book.css),
// le lien de collection passant au-dessus.
function RelatedProduct({ product, collectionLabels, allLabel }) {
  const [imageFailed, setImageFailed] = React.useState(false);
  const title = decodeHtmlEntities(product.title);
  const collectionLabel = product.collection
    ? decodeHtmlEntities((collectionLabels && collectionLabels[product.collection]) || product.collection)
    : '';

  return (
    <div className="book-related-card">
      {product.image && !imageFailed && (
        <img
          className="book-related-card__cover"
          src={product.image}
          alt=""
          loading="lazy"
          onError={() => setImageFailed(true)}
        />
      )}
      <div className="book-related-card__body">
        <a className="book-related-card__title" href={productPath(product.slug, product.collection, collectionLabels, allLabel)} title={title}>
          {title}
        </a>
        {product.reference && <span className="book-related-card__reference">{product.reference}</span>}
        {collectionLabel && (
          <a className="book-related-card__collection" href={collectionPath(product.collection, collectionLabels, allLabel)}>
            {collectionLabel}
          </a>
        )}
      </div>
    </div>
  );
}

// Accueil > collection > titre. La collection est celle de l'URL (/izvor/slug, ou « Toutes les
// collections » pour /tout/slug), sinon celle du livre (/book/slug, /P0225FR) : le serveur la
// transmet dans `book.breadcrumbCollection`. Libellé dans la langue de l'interface
// (`book.collectionLabels`, ajouté par le serveur).
function BookBreadcrumb({ lang, book, title, t, position = 'top' }) {
  const collection = book.breadcrumbCollection || book.collection || '';
  const labels = book.collectionLabels || {};
  const collectionLabel = collection
    ? decodeHtmlEntities(labels[collection] || (collection === ALL_COLLECTIONS ? t('collectionAll') : collection))
    : '';

  return (
    <nav className={`book-breadcrumb book-breadcrumb--${position}`} aria-label={t('breadcrumbLabel')}>
      <ol>
        <li>
          <a href={`/${lang}/`}>{t('breadcrumbHome')}</a>
        </li>
        {collection && (
          <li>
            <a href={collectionPath(collection, labels, t('collectionAll'))}>{collectionLabel}</a>
          </li>
        )}
        <li aria-current="page">{title}</li>
      </ol>
    </nav>
  );
}

export function BookPage({ lang, book }) {
  const { t } = useTranslation();

  if (!book) {
    return (
      <article className="book-page book-page--not-found">
        <h1>{t('bookNotFoundTitle')}</h1>
        <p>{t('bookNotFoundMessage')}</p>
        <a className="book-page__back" href={`/${lang}/`}>
          {t('bookBackHome')}
        </a>
      </article>
    );
  }

  const title = decodeHtmlEntities(book.title);

  // L'API book renvoie désormais `direction` sur le produit lui-même ; repli sur une détection par
  // script Unicode si le champ est absent/invalide (ancien contenu en cache).
  const direction = book.direction === 'rtl' || book.direction === 'ltr' ? book.direction : detectDirection(title);
  const contentsLines = String(book.contents || '')
    .split(/\r?\n/)
    .map((line) => decodeHtmlEntities(line.trim()))
    .filter(Boolean);

  return (
    <article className="book-page" dir={direction}>
      <BookBreadcrumb lang={lang} book={book} title={title} t={t} />

      <div className="book-page__main">
        {book.image && (
          <div className="book-page__cover">
            <img src={book.image} alt={title} />
          </div>
        )}
        <div className="book-page__info">
          <h1 className="book-page__title">{title}</h1>
          {book.description && <p className="book-page__meta">{decodeHtmlEntities(book.description)}</p>}
          {book.isbn && <p className="book-page__isbn">ISBN : {book.isbn}</p>}
          {book.summary && <p className="book-page__summary">{decodeHtmlEntities(book.summary)}</p>}

          {contentsLines.length > 0 && (
            <div className="book-page__contents">
              <h2>{t('bookContentsTitle')}</h2>
              <ul>
                {contentsLines.map((line, index) => (
                  <li key={index}>{line}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {Array.isArray(book.altreferences) && book.altreferences.some((product) => product && product.slug) && (
        <section className="book-page__related">
          <h2>{t('bookRelatedTitle')}</h2>
          <div className="book-related-grid">
            {book.altreferences
              .filter((product) => product && product.slug)
              .map((product) => (
                <RelatedProduct
                  product={product}
                  collectionLabels={book.collectionLabels}
                  allLabel={t('collectionAll')}
                  key={product.slug}
                />
              ))}
          </div>
        </section>
      )}

      {Array.isArray(book.translations) && book.translations.length > 0 && (
        <section className="book-page__translations">
          <h2>{t('bookTranslationsTitle')}</h2>
          <div className="book-translations-grid">
            {book.translations.map((translation) => {
              const translationTitle = decodeHtmlEntities(translation.title);
              return (
                <a
                  key={translation.slug}
                  className="book-translation-card"
                  href={`/book/${translation.slug}`}
                  title={translationTitle}
                >
                  <span className="book-translation-card__language">{decodeHtmlEntities(translation.language)}</span>
                  <span className="book-translation-card__title" dir={translation.direction || 'ltr'}>
                    {translationTitle}
                  </span>
                </a>
              );
            })}
          </div>
        </section>
      )}

      {Array.isArray(book.distributors) && book.distributors.length > 0 && (
        <section className="book-page__distributors">
          <h2>{t('bookDistributorsTitle')}</h2>
          <div className="book-distributors-grid">
            {book.distributors.map((distributor) => (
              <BookDistributor distributor={distributor} t={t} key={distributor.id} />
            ))}
          </div>
        </section>
      )}

      {/* Répété en bas de page : évite de remonter tout en haut pour revenir à la collection. */}
      <BookBreadcrumb lang={lang} book={book} title={title} t={t} position="bottom" />
    </article>
  );
}

// Même logique de déverrouillage que <Root> (public/js/app.js) : le rendu serveur n'a pas de
// sessionStorage et affiche donc toujours l'écran de mot de passe en premier (voir
// isSiteUnlocked()) — l'état initial doit être identique côté client pour ne pas casser
// l'hydratation (erreurs #418/#423), le vrai statut n'étant vérifié qu'après coup dans un effet.
export function BookRoot({ lang, book }) {
  const [unlocked, setUnlocked] = React.useState(false);

  React.useEffect(() => {
    if (isSiteUnlocked()) {
      setUnlocked(true);
    }
  }, []);

  if (!unlocked) {
    return <PasswordGate onUnlock={() => setUnlocked(true)} />;
  }

  return (
    <LanguageProvider initialLang={lang}>
      <div className="top-controls">
        <ThemeToggle />
        <LanguageSelector />
      </div>
      <BookPage lang={lang} book={book} />
      <ScrollToTop />
      <Footer />
      <CookieConsentManager />
    </LanguageProvider>
  );
}
